import { useState, useCallback } from 'react'
import { useSnapshot } from 'valtio'
import { useNavigate } from 'react-router-dom'
import {
  closeAppMenu,
  CurrentUserState,
  exitAppTour,
  resetFeedEditState,
  updateTipCreationListState,
  updateTipCreationState,
} from '@app/storage'
import { useTourStage } from '@app/helpers/hooks'
import { ROUTES } from '@app/constants'

export const useTipsterOnboarding = (onClose: (update: boolean) => void) => {
  const {
    isTouring,
    currentTourStage,
    tourStage,
    username,
    isTipster
  } = useSnapshot(CurrentUserState)

  const navigate = useNavigate()
  const [ _currentTourStage, setTourStage ] = useTourStage(currentTourStage)
  const [ showBasicVideo, setShowTourBasicVideo ] = useState(!tourStage.basicVideoExplainer)
  const [ showWelcome, setShowWelcome ] = useState(isTouring && !tourStage.basicWelcome)

  const closeCreation = useCallback((update: boolean) => {
    updateTipCreationState()
    updateTipCreationListState()
    resetFeedEditState()
    onClose(update)
  }, [ onClose ])

  const onBackTips = useCallback(
    () => window.history.length > 2 ? navigate(-1) : navigate(ROUTES.feed.short),
    [ navigate ]
  )

  const handleCloseModal = useCallback(() => {
    setShowTourBasicVideo(false)
  }, [])

  const handleWelcomeNext = useCallback(() => {
    setShowWelcome(false)
    setTourStage({ current: 'basicWelcome', next: 'basicVideoExplainer' })
  }, [ setTourStage ])

  const handleVideoNext = useCallback(() => {
    setTourStage({ current: 'contentVideoExplainer', next: 'userCompletedOnboarding' })
    CurrentUserState.currentTourStage = 'userCompletedOnboarding'
  }, [ setTourStage ])

  const handleSkip = useCallback(() => {
    setTourStage({ current: 'contentVideoExplainer', next: 'userCompletedOnboarding' })
    CurrentUserState.currentTourStage = 'userCompletedOnboarding'
  }, [ setTourStage ])

  const handleCompleteOnboarding = useCallback(() => {
    setTourStage({ current: 'startAppBanner', next: 'startAppBanner' })
    exitAppTour()
    closeAppMenu()
  }, [ setTourStage, closeCreation ])

  return {
    isTouring,
    isTipster,
    tourStage,
    currentTourStage,
    username,
    showBasicVideo,
    showWelcome,
    handlers: {
      handleCloseModal,
      handleWelcomeNext,
      handleVideoNext,
      handleSkip,
      handleCompleteOnboarding,
      onBackTips
    }
  }
}